import { getAccessToken } from 'lib/authToken'
import { routeToLoginIfUnauthorized } from 'lib/rawFetchAuth'
import { currentSessionSignal } from 'lib/accountSessionState'

/* Stops the request as soon as any of these signals fires, so the caller's own stop (a stop button,
 * leaving the page) and the account-session stop both count. Uses AbortSignal.any where the browser
 * has it (it cleans up after itself), and falls back to wiring the signals by hand where it doesn't. */
export const linkSignals = (signals: Array<AbortSignal | undefined>): AbortSignal => {
  const present = signals.filter((signal): signal is AbortSignal => Boolean(signal))
  const anyOf = (AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal }).any
  if (anyOf) {
    return anyOf(present)
  }
  const controller = new AbortController()
  for (const signal of present) {
    if (signal.aborted) {
      controller.abort()
      break
    }
    signal.addEventListener('abort', () => controller.abort(), { once: true })
  }
  return controller.signal
}

/* Signed-in fetch for the NexaAI stream requests that don't go through the shared axios instance.
 * Reads the current token as it sends, stops if the caller or the account session is cancelled, and
 * on a 401 sends the user back to sign in once. Never retried. */
export const accountScopedFetch = async (
  url: string,
  init: RequestInit = {},
  localSignal?: AbortSignal
): Promise<Response> => {
  const accessToken = getAccessToken() || ''
  const response = await fetch(url, {
    ...init,
    headers: {
      ...(init.headers || {}),
      Authorization: accessToken ? `Bearer ${accessToken}` : ''
    },
    signal: linkSignals([localSignal, currentSessionSignal()])
  })
  routeToLoginIfUnauthorized(response)
  return response
}
